/**
 * StatusWidget.js — Admin paneli Sistem sekmesi
 *
 * Mevcut endpoint'leri her 60 saniyede bir polling ederek canlı sistem
 * durumunu gösterir. Yeni backend endpoint gerektirmez.
 *
 * Polling edilen endpoint'ler:
 *   GET /health                  → DB + disk + saat durumu
 *   GET /api/sync/status         → Outbox pending count + son push
 *   GET /api/sync/dead-letters   → Kalıcı gönderilemez kayıt sayısı
 *   GET /api/sessions/stale      → 4 saatten uzun açık session'lar
 */

import React, { useState, useEffect, useCallback } from "react";
import { IcCash, IcCard } from "./Icons";

const API = process.env.REACT_APP_API_URL ?? "";
const POLL_INTERVAL = 60_000; // 60 saniye

// ── Yardımcı: fetch with timeout ──────────────────────────────────────────────
async function fetchJSON(url, options = {}) {
  const controller = new AbortController();
  const tid = setTimeout(() => controller.abort(), 8000);
  try {
    const res = await fetch(url, { ...options, signal: controller.signal });
    const data = await res.json();
    return { ok: res.ok, status: res.status, data };
  } catch (err) {
    return { ok: false, status: 0, data: null, error: err.message };
  } finally {
    clearTimeout(tid);
  }
}

// ── StatusCard bileşeni ───────────────────────────────────────────────────────
function StatusCard({ title, status, children }) {
  const borderColor =
    status === "ok"       ? "#4CAF50" :
    status === "warning"  ? "#f5a623" :
    status === "error"    ? "#e57373" :
    status === "disabled" ? "#555"    :
                            "#888";

  const badgeStyle = {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 700,
    background:
      status === "ok"       ? "#1a3a1a" :
      status === "warning"  ? "#3a2e0a" :
      status === "error"    ? "#3a1212" :
      status === "disabled" ? "#1e1e1e" :
                              "#222",
    color:
      status === "ok"       ? "#4CAF50" :
      status === "warning"  ? "#f5a623" :
      status === "error"    ? "#e57373" :
      status === "disabled" ? "#666"    :
                              "#888",
    marginLeft: 8,
    verticalAlign: "middle",
  };

  const badgeLabel =
    status === "ok"       ? "✓ Sağlıklı"   :
    status === "warning"  ? "⚠ Uyarı"     :
    status === "error"    ? "✕ Hata"       :
    status === "disabled" ? "— Devre Dışı" :
                            "— Bilinmiyor";

  return (
    <div style={{
      background: "#1a1a1a",
      border: `1.5px solid ${borderColor}`,
      borderRadius: 10,
      padding: "14px 18px",
      marginBottom: 14,
    }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: "#eee" }}>{title}</span>
        <span style={badgeStyle}>{badgeLabel}</span>
      </div>
      <div style={{ fontSize: 13, color: "#bbb", lineHeight: 1.8 }}>
        {children}
      </div>
    </div>
  );
}

// ── Row yardımcısı ────────────────────────────────────────────────────────────
function Row({ label, value, warn }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
      <span style={{ color: "#888" }}>{label}</span>
      <span style={{ color: warn ? "#f5a623" : "#ddd", fontWeight: warn ? 700 : 400 }}>
        {value ?? "—"}
      </span>
    </div>
  );
}

// ── Ana bileşen ───────────────────────────────────────────────────────────────
export default function SystemStatusTab() {
  const [health,      setHealth]      = useState(null);
  const [sync,        setSync]        = useState(null);
  const [deadLetters, setDeadLetters] = useState(null);
  const [stale,       setStale]       = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [lastPoll,    setLastPoll]    = useState(null);
  const [resetting,   setResetting]   = useState(false);
  const [resetMsg,    setResetMsg]    = useState(null);
  const [staleClosingId, setStaleClosingId] = useState(null);
  const [staleCloseMsg,  setStaleCloseMsg]  = useState(null);
  const [staleConfirm,   setStaleConfirm]   = useState(null); // {id, method}

  const poll = useCallback(async () => {
    setLoading(true);
    const [h, s, d, st] = await Promise.allSettled([
      fetchJSON(`${API}/health`),
      fetchJSON(`${API}/api/sync/status`),
      fetchJSON(`${API}/api/sync/dead-letters`),
      fetchJSON(`${API}/api/sessions/stale?idle_hours=4`),
    ]);

    setHealth(     h.status === "fulfilled" ? h.value : { ok: false, data: null });
    setSync(       s.status === "fulfilled" ? s.value : { ok: false, data: null });
    setDeadLetters(d.status === "fulfilled" ? d.value : { ok: false, data: null });
    setStale(      st.status === "fulfilled" ? st.value : { ok: false, data: null });
    setLastPoll(new Date());
    setLoading(false);
  }, []);

  useEffect(() => {
    poll();
    const id = setInterval(poll, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [poll]);

  // ── Tekil stale session'ı geriye dönük kapat ──────────────────────────────
  // Ödeme session'ın açılış tarihine yazılır → geriye dönük düzeltme,
  // aylık rapor doğru güne yansır.
  const handleStaleClose = async (sessionId, method) => {
    if (staleClosingId) return;
    setStaleClosingId(sessionId);
    setStaleCloseMsg(null);
    const result = await fetchJSON(
      `${API}/api/sessions/${sessionId}/force-close?payment_method=${method}`,
      { method: "POST", credentials: "include" }
    );
    if (result.ok && result.data) {
      setStaleCloseMsg({
        ok: true,
        text: `✓ #${sessionId} kapatıldı (${method === "CASH" ? "nakit" : "kart"}).`,
      });
    } else {
      setStaleCloseMsg({
        ok: false,
        text: result.data?.detail || `#${sessionId} kapatılamadı.`,
      });
    }
    setStaleClosingId(null);
    setStaleConfirm(null);
    setTimeout(() => setStaleCloseMsg(null), 5000);
    poll();
  };

  // ── Dead letter reset ──────────────────────────────────────────────────────
  const handleDeadLetterReset = async () => {
    if (resetting) return;
    setResetting(true);
    setResetMsg(null);
    const result = await fetchJSON(`${API}/api/sync/dead-letters/reset`, { method: "POST" });
    if (result.ok && result.data) {
      setResetMsg(`✓ ${result.data.reset_count} kayıt yeniden kuyruğa alındı`);
    } else {
      setResetMsg("✕ Reset başarısız");
    }
    setResetting(false);
    setTimeout(() => setResetMsg(null), 5000);
    poll(); // Durumu hemen yenile
  };

  // ── Genel durum hesapla ────────────────────────────────────────────────────
  const healthData      = health?.data;
  const syncData        = sync?.data;
  const deadLetterData  = deadLetters?.data;
  const staleData       = stale?.data;

  const healthStatus =
    !health?.ok                                  ? "error"   :
    healthData?.status === "degraded"            ? "error"   :
    healthData?.status === "warning"             ? "warning" :
                                                   "ok";

  const syncStatus =
    !sync?.ok                                    ? "error"    :
    syncData?.cloud_sync_enabled === false       ? "disabled" :
    (syncData?.pending_count ?? 0) > 50          ? "warning"  :
                                                   "ok";

  const deadLetterCount = deadLetterData?.count ?? 0;
  const deadLetterStatus =
    !deadLetters?.ok                             ? "error"   :
    deadLetterCount > 0                          ? "warning" :
                                                   "ok";

  const staleCount = staleData?.count ?? 0;
  const staleStatus =
    !stale?.ok                                   ? "error"   :
    staleCount > 0                               ? "warning" :
                                                   "ok";

  const backupOk  = healthData?.backup_ok;
  const backupAge = healthData?.backup_age_hours;
  const backupStatus =
    !health?.ok                                  ? "error"   :
    backupOk === false                           ? "error"   :
    backupOk === null || backupOk === undefined  ? "warning" : // henüz yedek yok
    backupAge > 25                               ? "warning" :
                                                   "ok";

  const overallBad =
    healthStatus === "error" || syncStatus === "error" ||
    deadLetterStatus === "error" || staleStatus === "error" ||
    backupStatus === "error";
  const overallWarn =
    healthStatus === "warning" || (syncStatus === "warning") ||
    deadLetterStatus === "warning" || staleStatus === "warning" ||
    backupStatus === "warning";

  const overallEmoji = overallBad ? "🔴" : overallWarn ? "🟡" : "🟢";
  const overallText  = overallBad ? "Sorun Var"  : overallWarn ? "Uyarı"    : "Tüm Sistemler Sağlıklı";
  const overallColor = overallBad ? "#e57373"    : overallWarn ? "#f5a623"  : "#4CAF50";

  return (
    <div style={{ padding: "24px 20px", maxWidth: 620 }}>
      {/* Başlık */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 22 }}>
        <div>
          <h2 style={{ margin: 0, color: "#eee", fontSize: 18 }}>Sistem Durumu</h2>
          <div style={{ fontSize: 12, color: "#666", marginTop: 3 }}>
            {lastPoll
              ? `Son güncelleme: ${lastPoll.toLocaleTimeString("tr-TR")} · Her 60s otomatik yenilenir`
              : "Yükleniyor..."}
          </div>
        </div>
        <button
          onClick={poll}
          disabled={loading}
          style={{
            background: "#2a2a2a",
            border: "1px solid #444",
            borderRadius: 8,
            color: "#ccc",
            padding: "6px 14px",
            cursor: loading ? "not-allowed" : "pointer",
            fontSize: 13,
          }}
        >
          {loading ? "⟳" : "↻ Yenile"}
        </button>
      </div>

      {/* Genel durum banner */}
      <div style={{
        background: "#111",
        border: `2px solid ${overallColor}`,
        borderRadius: 10,
        padding: "12px 18px",
        marginBottom: 20,
        display: "flex",
        alignItems: "center",
        gap: 12,
        fontSize: 15,
        fontWeight: 700,
        color: overallColor,
      }}>
        <span style={{ fontSize: 20 }}>{overallEmoji}</span>
        {overallText}
      </div>

      {/* ── Kart 1: Sistem Sağlığı ── */}
      <StatusCard title="Sistem Sağlığı" status={healthStatus}>
        {!health?.ok ? (
          <Row label="Backend" value="Erişilemiyor" warn />
        ) : (
          <>
            <Row label="DB"          value={healthData?.db}            warn={healthData?.db !== "ok"} />
            <Row label="Disk Durumu" value={healthData?.disk_status}   warn={healthData?.disk_status !== "ok"} />
            <Row label="Disk Boş"    value={healthData?.disk_free_mb != null ? `${healthData.disk_free_mb} MB` : undefined} warn={healthData?.disk_status !== "ok"} />
            <Row label="Versiyon"    value={healthData?.version} />
            <Row label="Saat"        value={healthData?.time ? new Date(healthData.time).toLocaleTimeString("tr-TR") : undefined}
                                     warn={!healthData?.clock_ok} />
          </>
        )}
      </StatusCard>

      {/* ── Kart 2: Cloud Sync ── */}
      <StatusCard title="Cloud Sync" status={syncStatus}>
        {!sync?.ok ? (
          <Row label="Durum" value="Erişilemiyor" warn />
        ) : syncData?.cloud_sync_enabled === false ? (
          <>
            <Row label="Durum" value="Devre dışı — CLOUD_SYNC_URL tanımlı değil" />
            <Row label="Outbox" value={`${syncData?.pending_count ?? 0} kayıt bekliyor (normal)`} />
          </>
        ) : (
          <>
            <Row label="Bekleyen Kayıt"  value={syncData?.pending_count ?? 0}  warn={(syncData?.pending_count ?? 0) > 50} />
            <Row label="Son Push"        value={syncData?.last_synced_at
              ? new Date(syncData.last_synced_at).toLocaleString("tr-TR")
              : "Henüz push yok"} />
          </>
        )}
      </StatusCard>

      {/* ── Kart 3: Dead Letters ── */}
      <StatusCard title="Dead Letter Kuyruğu" status={deadLetterStatus}>
        {!deadLetters?.ok ? (
          <Row label="Durum" value="Erişilemiyor" warn />
        ) : deadLetterCount === 0 ? (
          <Row label="Dead Letter" value="Yok — tüm kayıtlar gönderildi" />
        ) : (
          <>
            <Row label="Gönderilemez Kayıt" value={deadLetterCount} warn />
            <div style={{ marginTop: 10, fontSize: 12, color: "#aaa", lineHeight: 1.6 }}>
              Bu kayıtlar 5 deneme sonrası dead letter'a düştü.<br />
              Internet bağlantısını kontrol et, ardından resetle.
            </div>
            <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 12 }}>
              <button
                onClick={handleDeadLetterReset}
                disabled={resetting}
                style={{
                  background: "#3a1a1a",
                  border: "1px solid #e57373",
                  borderRadius: 7,
                  color: "#e57373",
                  padding: "6px 14px",
                  cursor: resetting ? "not-allowed" : "pointer",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {resetting ? "Sıfırlanıyor..." : "Dead Letter'ları Resetle"}
              </button>
              {resetMsg && (
                <span style={{ fontSize: 12, color: resetMsg.startsWith("✓") ? "#4CAF50" : "#e57373" }}>
                  {resetMsg}
                </span>
              )}
            </div>
          </>
        )}
      </StatusCard>

      {/* ── Kart 4: Yedekleme ── */}
      <StatusCard title="Yedekleme" status={backupStatus}>
        {!health?.ok ? (
          <Row label="Durum" value="Erişilemiyor" warn />
        ) : backupOk === null || backupOk === undefined ? (
          <Row label="Durum" value="Henüz yedek alınmamış" warn />
        ) : backupOk === false ? (
          <>
            <Row label="Durum" value="Son yedek BAŞARISIZ" warn />
            {healthData?.backup_error && (
              <Row label="Hata" value={healthData.backup_error} warn />
            )}
            {healthData?.backup_ts && (
              <Row label="Son Deneme" value={new Date(healthData.backup_ts).toLocaleString("tr-TR")} />
            )}
          </>
        ) : (
          <>
            <Row
              label="Son Yedek"
              value={healthData?.backup_ts ? new Date(healthData.backup_ts).toLocaleString("tr-TR") : "—"}
              warn={backupAge > 25}
            />
            <Row
              label="Geçen Süre"
              value={backupAge != null ? `${backupAge} saat önce` : "—"}
              warn={backupAge > 25}
            />
          </>
        )}
      </StatusCard>

      {/* ── Kart 5: Zombie Session'lar ── */}
      <StatusCard title="Açık Kalan Oturumlar (4+ saat)" status={staleStatus}>
        {!stale?.ok ? (
          <Row label="Durum" value="Erişilemiyor" warn />
        ) : staleCount === 0 ? (
          <Row label="Zombie Session" value="Yok — tüm oturumlar normal" />
        ) : (
          <>
            <Row label="Hareketsiz Oturum" value={staleCount} warn />
            <div style={{ marginTop: 8 }}>
              {(staleData?.sessions ?? []).map(s => {
                const isThisConfirm  = staleConfirm?.id === s.id;
                const isThisClosing  = staleClosingId === s.id;
                const anyClosing     = !!staleClosingId;
                return (
                  <div
                    key={s.id}
                    style={{
                      background: "#2a1a00",
                      borderRadius: 6,
                      padding: "8px 12px",
                      marginTop: 6,
                      fontSize: 12,
                      color: "#ddd",
                    }}
                  >
                    <div>
                      <span style={{ color: "#f5a623", fontWeight: 700 }}>
                        {s.table_label || s.table_code}
                      </span>
                      {" · "}{s.status}
                      {" · "}<span style={{ color: "#aaa" }}>
                        Toplam: {s.total_amount} ₺ / Ödenen: {s.paid_amount} ₺
                      </span>
                    </div>
                    {s.opened_at && (
                      <div style={{ color: "#777", marginTop: 2 }}>
                        Açılış: {new Date(s.opened_at).toLocaleString("tr-TR")}
                      </div>
                    )}

                    {isThisConfirm ? (
                      <div style={{
                        marginTop: 8,
                        padding: "8px 10px",
                        background: "rgba(245,166,35,0.08)",
                        border: "1px solid rgba(245,166,35,0.4)",
                        borderRadius: 6,
                      }}>
                        <div style={{ marginBottom: 6, color: "#f5a623" }}>
                          <strong>{staleConfirm.method === "CASH" ? "Nakit" : "Kart"}</strong> alındı olarak kapatılacak, ödeme tarihi <strong>{new Date(s.opened_at).toLocaleDateString("tr-TR")}</strong>. Emin misin?
                        </div>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            onClick={() => handleStaleClose(s.id, staleConfirm.method)}
                            disabled={isThisClosing}
                            style={{
                              background: "#f5a623", border: "none", color: "#1a1a1a",
                              borderRadius: 5, padding: "6px 12px", fontSize: 11,
                              fontWeight: 700, cursor: isThisClosing ? "not-allowed" : "pointer",
                            }}
                          >
                            {isThisClosing ? "Kapatılıyor…" : "Evet, kapat"}
                          </button>
                          <button
                            onClick={() => setStaleConfirm(null)}
                            disabled={isThisClosing}
                            style={{
                              background: "transparent", border: "1px solid #555",
                              color: "#aaa", borderRadius: 5, padding: "6px 12px",
                              fontSize: 11, cursor: "pointer",
                            }}
                          >
                            Vazgeç
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                        <button
                          onClick={() => setStaleConfirm({ id: s.id, method: "CASH" })}
                          disabled={anyClosing}
                          title="Nakit alındı olarak kapat"
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 5,
                            background: "rgba(76,175,80,0.12)",
                            border: "1px solid rgba(76,175,80,0.5)",
                            color: "#7bc47b",
                            borderRadius: 5, padding: "5px 10px", fontSize: 11,
                            fontWeight: 600, cursor: anyClosing ? "not-allowed" : "pointer",
                          }}
                        >
                          <IcCash size={13} /> Nakit alındı
                        </button>
                        <button
                          onClick={() => setStaleConfirm({ id: s.id, method: "CARD" })}
                          disabled={anyClosing}
                          title="Kart alındı olarak kapat"
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 5,
                            background: "rgba(129,140,248,0.12)",
                            border: "1px solid rgba(129,140,248,0.5)",
                            color: "#a5b4fc",
                            borderRadius: 5, padding: "5px 10px", fontSize: 11,
                            fontWeight: 600, cursor: anyClosing ? "not-allowed" : "pointer",
                          }}
                        >
                          <IcCard size={13} /> Kart alındı
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              {staleCloseMsg && (
                <div style={{
                  marginTop: 10, fontSize: 12,
                  color: staleCloseMsg.ok ? "#4CAF50" : "#e57373",
                  fontWeight: 600,
                }}>
                  {staleCloseMsg.text}
                </div>
              )}
            </div>
          </>
        )}
      </StatusCard>
    </div>
  );
}
